import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh as ImproveIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { aiBooksApi, aiCodeHelpApi, AiBook } from '../services/api';
import { renderError } from '../utils/errorUtils';

const SAMPLE = `function add(a, b) {
  return a + b
}`;

const AICodeHelpPanel: React.FC = () => {
  const [books, setBooks] = useState<AiBook[]>([]);
  const [selectedBookIds, setSelectedBookIds] = useState<number[]>([]);
  const [code, setCode] = useState(SAMPLE);
  const [question, setQuestion] = useState('Make this clearer for beginners');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openaiConfigured, setOpenaiConfigured] = useState<boolean | null>(null);
  const [result, setResult] = useState<{
    provider: string;
    improved_code: string;
    explanation: string;
    suggestions: string[];
    books_used?: { book_id: number; title: string }[];
  } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [booksRes, statusRes] = await Promise.all([
          aiBooksApi.list(),
          aiCodeHelpApi.status(),
        ]);
        setBooks(booksRes.data.books || []);
        setOpenaiConfigured(Boolean(statusRes.data.openai_configured));
        const withText = (booksRes.data.books || [])
          .filter((b) => Number(b.has_text))
          .map((b) => b.book_id);
        setSelectedBookIds(withText.slice(0, 3));
      } catch (err: any) {
        setError(renderError(err.response?.data?.error || 'Failed to load code-help status'));
      }
    })();
  }, []);

  const toggleBook = (bookId: number) => {
    setSelectedBookIds((prev) =>
      prev.includes(bookId) ? prev.filter((id) => id !== bookId) : [...prev, bookId]
    );
  };

  const handleImprove = async () => {
    try {
      setLoading(true);
      setError(null);
      setResult(null);
      const res = await aiCodeHelpApi.improve({
        code,
        question,
        book_ids: selectedBookIds,
      });
      setResult(res.data);
    } catch (err: any) {
      setError(
        renderError(err.response?.data?.details || err.response?.data?.error || 'Improve failed')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CodeIcon color="primary" />
        Improve this code
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Paste code and optionally select uploaded books as context. Uses OpenAI when configured;
        otherwise local heuristic suggestions.
      </Typography>

      {openaiConfigured != null && (
        <Chip
          size="small"
          sx={{ mb: 2 }}
          color={openaiConfigured ? 'success' : 'warning'}
          label={openaiConfigured ? 'OpenAI configured' : 'Fallback mode (no OPENAI_API_KEY)'}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={2} sx={{ maxWidth: 900 }}>
        {books.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Books for context
            </Typography>
            <FormGroup row>
              {books.map((book) => (
                <FormControlLabel
                  key={book.book_id}
                  control={
                    <Checkbox
                      checked={selectedBookIds.includes(book.book_id)}
                      onChange={() => toggleBook(book.book_id)}
                      disabled={!Number(book.has_text)}
                    />
                  }
                  label={`${book.title}${Number(book.has_text) ? '' : ' (no text)'}`}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        <TextField
          label="Question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          fullWidth
          size="small"
        />
        <TextField
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          fullWidth
          multiline
          minRows={8}
          InputProps={{ sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } }}
        />
        <Button
          variant="contained"
          onClick={handleImprove}
          disabled={loading || !code.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <ImproveIcon />}
          sx={{ alignSelf: 'flex-start' }}
        >
          Improve this code
        </Button>

        {result && (
          <Box sx={{ mt: 1 }}>
            <Chip size="small" label={`Provider: ${result.provider}`} sx={{ mb: 1, mr: 1 }} />
            {(result.books_used || []).map((b) => (
              <Chip key={b.book_id} size="small" label={b.title} sx={{ mb: 1, mr: 1 }} />
            ))}
            <Typography variant="subtitle2" gutterBottom>
              Explanation
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {result.explanation}
            </Typography>
            {result.suggestions?.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Suggestions
                </Typography>
                <Box component="ul" sx={{ mt: 0, mb: 2 }}>
                  {result.suggestions.map((s, i) => (
                    <li key={i}>
                      <Typography variant="body2">{s}</Typography>
                    </li>
                  ))}
                </Box>
              </>
            )}
            <Typography variant="subtitle2" gutterBottom>
              Improved code
            </Typography>
            <TextField
              value={result.improved_code}
              fullWidth
              multiline
              minRows={6}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
              }}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default AICodeHelpPanel;
